import { UserName } from "../components/UserName.tsx";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { MAX_ATTEMPTS, STANDARD_DUEL_RULES, type DuelSessionScore, type MatchRecord } from "@mk/shared";
import { Board } from "../components/Board.tsx";
import { ResultBoard } from "../components/ResultBoard.tsx";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { DuelClock } from "../components/DuelClock.tsx";
import { GhostBoard } from "../components/GhostBoard.tsx";
import { Keyboard } from "../components/Keyboard.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { ShareButton } from "../components/ShareButton.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Ticker } from "../components/Ticker.tsx";
import { Toast } from "../components/Toast.tsx";
import { WordMeaning } from "../components/WordMeaning.tsx";
import { useDuel } from "../features/duel/useDuel.ts";
import { useLobbyStats } from "../features/duel/useLobbyStats.ts";
import { useBoardSelectionHint } from "../features/useBoardSelectionHint.ts";
import { duelResultText } from "../features/duel/resultText.ts";
import {
  duelRulesSummary,
  isStandardDuelRules,
  victoryRuleLabel,
} from "../features/duel/rules.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useSession } from "../lib/session.ts";
import { messages } from "../i18n/index.ts";
import { gameEdition, upperLabel } from "../lib/edition.ts";
import { leaveResultThroughAd } from "../lib/adGate.ts";
import { api, post, ApiError } from "../lib/api.ts";
import { getSocket } from "../lib/socket.ts";
import { ResultEffects } from "../components/ResultEffects.tsx";

interface DuelScreenProps {
  onBack: () => void;
  onExitConfirmed: () => void;
  registerBackHandler: RegisterBackHandler;
  onFriends?: () => void;
  /** Ekran açılır açılmaz rakip aramaya başla (ana sayfadaki "Rakip bul"). */
  autoSearch?: boolean;
  onAutoSearchHandled?: () => void;
}

/** Bu kadar saniye sonra "kimse yok" demek dürüstlük olur. */
const SEARCH_PATIENCE_SEC = 20;

function recordText(record: MatchRecord | undefined): string {
  if (!record) return "—";
  const rate = record.played ? `${Math.round(100 * record.wins / record.played)}%` : "—";
  return messages.duel.record(record.played, record.wins, record.losses, record.draws, rate);
}

function ResultFriendAction({ opponentId }: { opponentId: string }) {
  const [status, setStatus] = useState<"loading" | "none" | "outgoing" | "incoming" | "accepted">("loading");
  const [notice, setNotice] = useState<string | null>(null);
  const busy = useRef(false);
  useEffect(() => {
    let active = true;
    const refresh = () => { void api<{ status: typeof status }>(`/api/friends/status/${opponentId}`)
      .then((data) => { if (active) setStatus(data.status); }).catch(() => {}); };
    refresh();
    const socket = getSocket();
    socket.on("friends:changed", refresh);
    return () => { active = false; socket.off("friends:changed", refresh); };
  }, [opponentId]);
  const label = status === "accepted" ? messages.duel.alreadyFriends
    : status === "outgoing" ? messages.duel.friendRequestSent
      : status === "incoming" ? messages.duel.acceptFriend
        : messages.duel.addFriend;
  return <div className="mt-3">
    <button type="button" className="btn-secondary w-full" disabled={status === "loading" || status === "accepted" || status === "outgoing"}
      onClick={() => {
        if (busy.current) return;
        busy.current = true;
        setNotice(null);
        void post<{ status: "pending" | "accepted" }>("/api/friends/request", { userId: opponentId })
          .then((response) => setStatus(response.status === "accepted" ? "accepted" : "outgoing"))
          .catch((error: unknown) => setNotice(error instanceof ApiError ? error.message : messages.auth.connectionFailed))
          .finally(() => { busy.current = false; });
      }}>{label}</button>
    {notice && <p role="alert" className="mt-2 text-xs text-flame">{notice}</p>}
  </div>;
}

function SessionScore({ score, opponentName, prominent = false }: {
  score: DuelSessionScore;
  opponentName: string;
  prominent?: boolean;
}) {
  return (
    <section className={prominent ? "session-score session-score--result" : "session-score"}>
      <span className="session-score__title">{messages.duel.sessionScoreTitle}</span>
      <div className="session-score__matchup">
        <span className="session-score__player" data-side="you">
          <small>{messages.common.you}</small>
          <b>{score.yourWins}</b>
        </span>
        <span className="session-score__divider" aria-hidden="true">–</span>
        <span className="session-score__player" data-side="opponent" title={opponentName}>
          <b>{score.opponentWins}</b>
          <small className="username">{opponentName}</small>
        </span>
      </div>
      {score.draws > 0 && (
        <span className="session-score__draws">{messages.duel.sessionDraws(score.draws)}</span>
      )}
    </section>
  );
}

export function DuelScreen({
  onBack,
  onExitConfirmed,
  registerBackHandler,
  onFriends,
  autoSearch = false,
  onAutoSearchHandled,
}: DuelScreenProps) {
  const duel = useDuel();
  const autoSearchStarted = useRef(false);

  useEffect(() => {
    if (!autoSearch || autoSearchStarted.current) return;
    autoSearchStarted.current = true;
    onAutoSearchHandled?.();
    // Devam eden bir maç ya da arama varsa ona dokunma; yalnız boşta başlat.
    if (duel.phase === "idle") duel.findOpponent();
  }, [autoSearch, duel, onAutoSearchHandled]);
  const selectionHint = useBoardSelectionHint(
    duel.phase === "live" && !duel.myTurnOver && !duel.opponentWatching ? duel.pinned : null,
  );
  const lobby = useLobbyStats();
  const me = useSession((s) => s.user);
  const stats = useSession((s) => s.stats);
  const [confirmKind, setConfirmKind] = useState<"search" | "forfeit" | null>(null);
  const [exitBusy, setExitBusy] = useState(false);
  const [searchMs, setSearchMs] = useState(0);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [finishedKeyboardOpen, setFinishedKeyboardOpen] = useState(false);

  useEffect(() => {
    setFinishedKeyboardOpen(false);
  }, [duel.match?.matchId]);

  const shareApp = async () => {
    const url = window.location.origin;
    if (navigator.share) {
      try {
        await navigator.share({ title: gameEdition.productName, url });
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareNotice(messages.duel.shared);
    } catch {
      setShareNotice(messages.share.failed);
    }
    window.setTimeout(() => setShareNotice(null), 1800);
  };

  // Ne kadardır aradığımız — "bir şey oluyor mu" sorusunun cevabı.
  useEffect(() => {
    if (duel.phase !== "searching") {
      setSearchMs(0);
      return;
    }
    const startedAt = Date.now();
    const id = window.setInterval(() => setSearchMs(Date.now() - startedAt), 1000);
    return () => window.clearInterval(id);
  }, [duel.phase]);

  useEffect(
    () =>
      registerBackHandler(() => {
        if (confirmKind) {
          if (!exitBusy) setConfirmKind(null);
          return "block";
        }
        if (duel.phase === "over") {
          void leaveResultThroughAd().then(duel.reset);
          return "block";
        }
        if (duel.phase === "searching") {
          setConfirmKind("search");
          return "block";
        }
        if (duel.phase === "countdown" || duel.phase === "live") {
          setConfirmKind("forfeit");
          return "block";
        }
        return "allow";
      }),
    [confirmKind, duel.phase, duel.reset, exitBusy, registerBackHandler],
  );

  const exitDialog = (
    <ConfirmDialog
      open={confirmKind !== null}
      title={confirmKind === "forfeit" ? messages.duel.leaveTitle : messages.duel.stopTitle}
      message={
        confirmKind === "forfeit"
          ? messages.duel.leaveMessage
          : messages.duel.stopMessage
      }
      confirmLabel={confirmKind === "forfeit" ? messages.duel.forfeit : messages.duel.stop}
      // Maçtan çıkmak geri alınamaz: düğme üç saniye kilitli kalsın.
      confirmDelayMs={confirmKind === "forfeit" ? 3000 : 0}
      busy={exitBusy}
      onCancel={() => setConfirmKind(null)}
      onConfirm={() => {
        if (confirmKind === "search") {
          duel.cancelSearch();
          setConfirmKind(null);
          onExitConfirmed();
          return;
        }
        if (confirmKind === "forfeit") {
          setExitBusy(true);
          void duel.forfeit().then((exited) => {
            setExitBusy(false);
            if (!exited) return;
            setConfirmKind(null);
            onExitConfirmed();
          });
        }
      }}
    />
  );

  /* ---------------- Lobi: boşta ya da rakip aranıyor ---------------- */

  if (duel.phase === "idle" || duel.phase === "searching") {
    const searching = duel.phase === "searching";
    /*
     * Kuyruk uzunluğunu değil çevrimiçi sayısını gösteriyoruz: kuyruğa ikinci
     * kişi girer girmez eşleşme olduğu için uzunluk hep 0 ya da 1, oyuncuya
     * hiçbir şey anlatmaz. Asıl soru "ortalıkta kimse var mı".
     */
    const online = lobby?.online ?? null;
    const alone = online !== null && online <= 1;
    const waited = Math.floor(searchMs / 1000);

    return (
      <div className="app">
        <ScreenHeader title={messages.duel.title} onBack={onBack} />
        <main className="relative grid place-items-center overflow-hidden px-6 text-center">
          <div className="stripes stripes--wide stripes--moving" aria-hidden="true" />
          <Toast message={shareNotice ?? duel.toast} />
          <div className="relative flex max-w-xs flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="live-dot" aria-hidden="true" />
              <span className="eyebrow eyebrow--live">
                {searching ? messages.duel.queue : messages.duel.matchmakingOpen}
              </span>
            </div>

            <p className="scoreboard text-[2.75rem]">
              {searching ? messages.duel.searching : messages.duel.challenge}
            </p>

            <p className="text-sm leading-relaxed text-muted">
              {searching
                ? messages.duel.searchHint
                : messages.duel.intro(MAX_ATTEMPTS)}
            </p>

            {/*
             * Sayı gelmediyse hiçbir şey yazmıyoruz: "0 oyuncu çevrimiçi"
             * demek, sayaç henüz ulaşmadığı için yanlış olurdu.
             */}
            {online !== null && (
              <p className="lobby-count" role="status">
                {alone ? (
                  <>
                    {gameEdition.id === "en" ? messages.duel.early : messages.duel.nobodyOnline}
                    <span className="lobby-count__hint">
                      {messages.duel.aloneHint}
                    </span>
                  </>
                ) : (
                  <>
                    {messages.duel.playersOnline(online)}
                  </>
                )}
              </p>
            )}

            {searching ? (
              <div className="flex flex-col items-center gap-3">
                <p className="text-xs text-muted" role="status">
                  {waited < 60
                    ? messages.duel.searchingSeconds(waited)
                    : messages.duel.searchingMinutes(Math.floor(waited / 60))}
                  {/* Sayı sunucudan geliyor: uydurmadan, gerçekten yalnızsa söylüyoruz. */}
                  {waited >= SEARCH_PATIENCE_SEC &&
                    (lobby?.queued ?? 2) <= 1 &&
                    ` · ${messages.duel.queueEmpty}`}
                </p>
                <button type="button" className="btn-secondary" onClick={duel.cancelSearch}>
                  {messages.duel.stopSearch}
                </button>
                {alone && gameEdition.id === "en" && (
                  <div className="flex flex-wrap justify-center gap-2">
                    {onFriends && (
                      <button type="button" className="chip-btn" onClick={onFriends}>
                        {messages.duel.inviteFriend}
                      </button>
                    )}
                    <button type="button" className="chip-btn" onClick={() => void shareApp()}>
                      {messages.duel.shareApp}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button type="button" className="btn-flame" onClick={duel.findOpponent}>
                  {messages.duel.findOpponent}
                </button>
                <div className="tally">
                  <div>
                    <b className="text-mint">{stats?.duelWins ?? 0}</b>
                    <span>{messages.duel.wins}</span>
                  </div>
                  <div>
                    <b className="text-flame">{stats?.duelLosses ?? 0}</b>
                    <span>{messages.duel.losses}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
        {exitDialog}
      </div>
    );
  }

  /* ---------------- Eşleşme bulundu: VS ekranı ---------------- */

  const opponentName = duel.match?.opponent.username ?? messages.duel.opponent;
  const matchRules = duel.match?.rules ?? STANDARD_DUEL_RULES;
  const friendlyMatch = duel.match?.category === "friendly";

  if (duel.phase === "countdown") {
    const seconds = Math.max(1, Math.ceil(duel.countdownMs / 1000));
    return (
      <div className="app">
        <ScreenHeader title={friendlyMatch ? messages.duel.friendlyTitle : messages.duel.title} onBack={onBack} />
        <main className="relative grid place-items-center overflow-hidden px-6">
          <div className="stripes stripes--wide stripes--moving" aria-hidden="true" />
          <div className="relative flex w-full flex-col items-center gap-7">
            <div className="vs-grid">
              <div className="plate-l vs-left">
                <div className="vs-name">{me?.username ?? messages.common.you}</div>
                <div className="vs-record">{recordText(duel.match?.records?.you)}</div>
              </div>
              <span className="vs-mark">VS</span>
              <div className="plate-r vs-right">
                <div className="vs-name username">{opponentName}</div>
                <div className="vs-record">{recordText(duel.match?.records?.opponent)}</div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <span className="eyebrow">{messages.duel.matchFound}</span>
              <span className="vs-count">{seconds}</span>
            </div>

            <div className="vs-rules">
              <span>{messages.duel.sameWord}</span>
              <span>{messages.duel.minuteCount(matchRules.durationMinutes)}</span>
              <span>{messages.duel.attemptCount(matchRules.maxAttempts)}</span>
              <span>{upperLabel(victoryRuleLabel(matchRules.victoryRule))}</span>
            </div>
          </div>
        </main>
        {exitDialog}
      </div>
    );
  }

  /* ---------------- Canlı maç ve sonuç ---------------- */

  const result = duel.result;
  const won = result ? result.winnerId === me?.id : false;
  const draw = result ? result.winnerId === null : false;
  // Sonuç panelinin rol rengi: zafer mint, beraberlik amber, yenilgi alev.
  const lost = result !== null && !won && !draw;
  const resultAccent = draw ? "amber" : lost ? "flame" : "mint";
  const accentVar = `var(--color-${resultAccent})`;
  const primaryVariant =
    resultAccent === "flame"
      ? "btn-primary--flame"
      : resultAccent === "amber"
        ? "btn-primary--amber"
        : "";
  const chipVariant =
    resultAccent === "flame"
      ? "chip-btn--flame"
      : resultAccent === "amber"
        ? "chip-btn--amber"
        : "chip-btn--primary";

  const tickerText = duel.opponentSolvedAt
    ? messages.duel.tickerSolved(opponentName)
    : duel.opponentRows.length > 0
      ? messages.duel.tickerGuess(opponentName, duel.opponentRows.length)
      : messages.duel.tickerPreparing(opponentName);
  const opponentAttemptLimit =
    duel.opponentSolvedAt ?? (duel.solved ? duel.rows.length : matchRules.maxAttempts);
  const canWatchOpponent =
    duel.phase === "live" && duel.myTurnOver && duel.opponentSolvedAt === null;
  const opponentDraftCells =
    duel.opponentWatch?.draft?.cells.map((cell) => cell ?? undefined) ??
    Array.from({ length: 5 }, () => undefined);

  return (
    <div className="app app--duel-live">
      <ScreenHeader
        title={friendlyMatch ? messages.duel.friendlyTitle : messages.duel.title}
        onBack={onBack}
        actions={
          <span className="flex items-center gap-2.5">
            {duel.watchers.total > 0 && (
              <span className="eyebrow text-muted" role="status">
                {messages.duel.watching(duel.watchers.total)}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="live-dot" aria-hidden="true" />
              <span className="eyebrow eyebrow--live">{messages.duel.live}</span>
            </span>
          </span>
        }
      />

      <div>
        <div className="duel-strip">
          <div className="plate-l duel-side">
            <span className="duel-side__label">{messages.common.you}</span>
            <span className="duel-side__value">
              {duel.rows.length}
              <span className="rest">/{duel.attemptLimit}</span>
            </span>
          </div>
          <DuelClock remainingMs={duel.remainingMs} />
          <div
            className="plate-r duel-side"
            style={
              duel.opponentSolvedAt !== null
                ? ({ ["--edge"]: "var(--color-mint)" } as CSSProperties)
                : undefined
            }
          >
            <span className="duel-side__label username">{opponentName}</span>
            <span
              className="duel-side__value"
              style={{
                color:
                  duel.opponentSolvedAt !== null ? "var(--color-mint)" : "var(--color-flame)",
              }}
            >
              {duel.opponentRows.length}
              <span className="rest">/{opponentAttemptLimit}</span>
            </span>
          </div>
        </div>
        {/*
          * Kural satırı yalnızca standarttan sapan maçlarda. Standart maçta
          * hiçbir yeni bilgi vermiyor ama üst panelden yer alıyor ve tahta
          * kalan alana göre ölçüldüğü için doğrudan kutucukları küçültüyor.
          */}
        {!isStandardDuelRules(matchRules) && (
          <p className="pb-1 text-center text-[0.65rem] font-bold tracking-wide text-dim">
            {upperLabel(duelRulesSummary(matchRules))}
          </p>
        )}

        <div className="px-4 pb-1.5">
          {canWatchOpponent ? (
            <button
              type="button"
              className="opponent-watch-trigger"
              aria-pressed={duel.opponentWatching}
              aria-label={messages.duel.watchLabel(opponentName)}
              disabled={duel.opponentWatchBusy}
              onClick={duel.watchOpponent}
            >
              <GhostBoard
                rows={duel.opponentRows}
                label={opponentName}
                solvedAt={duel.opponentSolvedAt}
                attempts={duel.opponentRows.length}
                maxAttempts={matchRules.maxAttempts}
                attemptLimit={opponentAttemptLimit}
                showCount={false}
                hint={
                  duel.opponentWatching
                    ? messages.duel.watchOpen
                    : messages.duel.watchPrompt
                }
              />
            </button>
          ) : (
            <GhostBoard
              rows={duel.opponentRows}
              label={opponentName}
              solvedAt={duel.opponentSolvedAt}
              attempts={duel.opponentRows.length}
              maxAttempts={matchRules.maxAttempts}
              attemptLimit={opponentAttemptLimit}
              showCount={false}
            />
          )}
          <Ticker text={tickerText} />
        </div>
      </div>

      <main className="relative grid min-h-0 place-items-center px-3">
        <Toast message={duel.toast ?? selectionHint} />
        {duel.opponentWatching ? (
          duel.opponentWatch ? (
            <div className="opponent-watch-view">
              <p className="opponent-watch-view__label" role="status">
                {messages.duel.liveLetters(opponentName)}
              </p>
              <Board
                rows={duel.opponentWatch.guesses}
                cells={opponentDraftCells}
                shake={false}
                won={false}
                maxAttempts={matchRules.maxAttempts}
                attemptLimit={duel.opponentWatch.attemptLimit}
              />
            </div>
          ) : (
            <p className="text-sm text-muted" role="status">
              {messages.duel.openingBoard}
            </p>
          )
        ) : (
          <Board
            rows={duel.rows}
            cells={duel.cells}
            pinned={duel.pinned}
            onSelectCell={duel.selectCell}
            shake={duel.shake}
            won={duel.solved}
            maxAttempts={matchRules.maxAttempts}
            attemptLimit={duel.attemptLimit}
          />
        )}
      </main>

      {duel.myTurnOver && duel.phase === "live" ? (
        duel.opponentWatching ? (
          <div className="finish-bar">
            <button type="button" className="btn-secondary w-full" onClick={duel.unwatchOpponent}>
              {messages.duel.ownBoard}
            </button>
          </div>
        ) : (
          <div className="finished-keyboard" data-open={finishedKeyboardOpen ? "true" : undefined}>
            <button
              type="button"
              className="finished-keyboard__toggle"
              aria-expanded={finishedKeyboardOpen}
              onClick={() => setFinishedKeyboardOpen((open) => !open)}
            >
              <span className="finished-keyboard__icon" aria-hidden="true">⌨</span>
              <span className="finished-keyboard__copy">
                <b>
                  {finishedKeyboardOpen
                    ? messages.duel.hideKeyboard
                    : messages.duel.showKeyboard}
                </b>
                <small>{messages.duel.keyboardReviewHint}</small>
              </span>
              <span className="finished-keyboard__chevron" aria-hidden="true">⌃</span>
            </button>
            {finishedKeyboardOpen && (
              <div className="finished-keyboard__keys">
                <Keyboard states={duel.keyboard} onPress={() => {}} disabled />
              </div>
            )}
          </div>
        )
      ) : (
        <Keyboard
          states={duel.keyboard}
          onPress={duel.press}
          disabled={duel.phase !== "live" || duel.myTurnOver}
          busy={duel.sending}
        />
      )}

      <Sheet
        open={duel.phase === "over" && result !== null && duel.ownTurnRevealed}
        title={draw ? messages.duel.draw : won ? messages.duel.won : messages.duel.lost}
        accent={resultAccent}
        placement="center"
        onClose={() => { void leaveResultThroughAd().then(duel.reset); }}
      >
        {result && (
          <>
            {duel.ownTurnRevealed && <ResultEffects matchId={result.matchId} outcome={draw ? "draw" : won ? "win" : "loss"} />}
            <div
              className="stripes stripes--wide stripes--moving"
              style={{ ["--stripe"]: accentVar } as CSSProperties}
              aria-hidden="true"
            />

            <div className="relative">
              <span className="eyebrow" style={{ color: accentVar }}>
                {messages.duel.matchOver}
              </span>
              <p className="mt-1 text-sm text-muted">
                {duelResultText(result, me?.id)}
              </p>
              <p className="mt-2 text-xs text-dim">{duelRulesSummary(result.rules)}</p>

              {result.sessionScore && (
                <div className="mt-4">
                  <SessionScore
                    score={result.sessionScore}
                    opponentName={result.opponent.user.username}
                    prominent
                  />
                </div>
              )}

              <div className="result-rematch">
                {result.reason === "forfeit" ? null : duel.rematchOffer ? (
                  <div className="rematch-offer">
                    <p>
                      {messages.duel.rematchRequest(duel.rematchOffer.from.username)}
                    </p>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        className="chip-btn"
                        onClick={() => duel.answerRematch(false)}
                      >
                        {messages.rematch.decline}
                      </button>
                      <button
                        type="button"
                        className={`chip-btn ${chipVariant}`}
                        onClick={() => { void leaveResultThroughAd().then(() => duel.answerRematch(true)); }}
                      >
                        {messages.rematch.accept}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`btn-primary w-full ${primaryVariant}`}
                    onClick={() => { if (duel.rematchSentFor !== result.matchId) void leaveResultThroughAd().then(duel.askRematch); }}
                    disabled={duel.rematchSentFor === result.matchId}
                  >
                    {duel.rematchSentFor === result.matchId
                      ? messages.duel.rematchSent
                      : messages.duel.askRematch}
                  </button>
                )}

                {duel.rematchNotice && (
                  <p className="mt-2 text-center text-xs text-muted" role="status">
                    {duel.rematchNotice}
                  </p>
                )}
              </div>

              {/* Rövanştan sonra: ana eylem önde, arkadaş ekleme ikincil. */}
              <ResultFriendAction opponentId={result.opponent.user.id} />

              <div className="answer-row mt-4">
                {[...result.answer].map((letter, i) => (
                  <span key={i} className="tile" data-filled="true" data-state="correct">
                    {letter}
                  </span>
                ))}
              </div>

              <div className="mt-4">
                <WordMeaning word={result.answer} />
              </div>

              <div className="duel-result-grid mt-6">
                <div className="duel-result-card plate-l">
                  <span className="duel-compare__name">
                    {messages.common.you} <span className="text-mint">· {result.you.attempts}</span>
                  </span>
                  <ResultBoard rows={duel.rows} />
                </div>
                <div className="duel-result-card plate-r">
                  <span className="duel-compare__name">
                    {<UserName user={result.opponent.user} />}{" "}
                    <span className="text-flame">· {result.opponent.attempts}</span>
                  </span>
                  <ResultBoard rows={result.opponent.guesses} />
                </div>
              </div>

              {/*
               * Düellonun kelimesi o maça özel; harfleri göstermenin kimseye
               * spoiler'ı yok, o yüzden `spoilerRisk` verilmiyor.
               */}
              <div className="mt-5">
                <ShareButton
                  result={{
                    mode: "duel",
                    opponentName: result.opponent.user.username,
                    won,
                    draw,
                    yourAttempts: result.you.attempts,
                    opponentAttempts: result.opponent.attempts,
                    solved: result.you.solved,
                  }}
                  rows={duel.rows}
                  opponentRows={result.opponent.guesses}
                />
              </div>

              {/* Kaydırmadan erişilsin diye pencerenin altına yapışık. */}
              <div className="result-footer flex gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    void leaveResultThroughAd().then(() => { duel.reset(); onExitConfirmed(); });
                  }}
                >
                  {messages.duel.home}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    void leaveResultThroughAd().then(() => { duel.reset(); duel.findOpponent(); });
                  }}
                >
                  {messages.duel.newOpponent}
                </button>
              </div>
            </div>
          </>
        )}
      </Sheet>
      {exitDialog}
    </div>
  );
}
